// TaskFlow · editor HTML enriquecido colaborativo (TipTap + Yjs).
// Mismo editor para notas y descripciones de tareas. Funciona sin conexión: el documento se guarda
// en el dispositivo (IndexedDB) y se mezcla con el del servidor al reconectar, sin conflictos.

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import { e, icono } from './util.js';

const BOTONES = [
  { id: 'negrita', etiqueta: 'Negrita', atajo: 'Ctrl+B', texto: '<b>N</b>', activo: (ed) => ed.isActive('bold'), accion: (ed) => ed.chain().focus().toggleBold().run() },
  { id: 'cursiva', etiqueta: 'Cursiva', atajo: 'Ctrl+I', texto: '<i>C</i>', activo: (ed) => ed.isActive('italic'), accion: (ed) => ed.chain().focus().toggleItalic().run() },
  { id: 'subrayado', etiqueta: 'Subrayado', atajo: 'Ctrl+U', texto: '<u>S</u>', activo: (ed) => ed.isActive('underline'), accion: (ed) => ed.chain().focus().toggleUnderline().run() },
  { id: 'tachado', etiqueta: 'Tachado', atajo: 'Ctrl+Shift+S', texto: '<s>T</s>', activo: (ed) => ed.isActive('strike'), accion: (ed) => ed.chain().focus().toggleStrike().run() },
  '|',
  { id: 'h1', etiqueta: 'Título 1', atajo: 'Ctrl+Alt+1', texto: 'T1', activo: (ed) => ed.isActive('heading', { level: 1 }), accion: (ed) => ed.chain().focus().toggleHeading({ level: 1 }).run() },
  { id: 'h2', etiqueta: 'Título 2', atajo: 'Ctrl+Alt+2', texto: 'T2', activo: (ed) => ed.isActive('heading', { level: 2 }), accion: (ed) => ed.chain().focus().toggleHeading({ level: 2 }).run() },
  { id: 'h3', etiqueta: 'Título 3', atajo: 'Ctrl+Alt+3', texto: 'T3', activo: (ed) => ed.isActive('heading', { level: 3 }), accion: (ed) => ed.chain().focus().toggleHeading({ level: 3 }).run() },
  '|',
  { id: 'vinetas', etiqueta: 'Lista con viñetas', atajo: 'Ctrl+Shift+8', texto: '•≡', activo: (ed) => ed.isActive('bulletList'), accion: (ed) => ed.chain().focus().toggleBulletList().run() },
  { id: 'numerada', etiqueta: 'Lista numerada', atajo: 'Ctrl+Shift+7', texto: '1≡', activo: (ed) => ed.isActive('orderedList'), accion: (ed) => ed.chain().focus().toggleOrderedList().run() },
  { id: 'cita', etiqueta: 'Cita', atajo: 'Ctrl+Shift+B', texto: '❝', activo: (ed) => ed.isActive('blockquote'), accion: (ed) => ed.chain().focus().toggleBlockquote().run() },
  { id: 'codigo', etiqueta: 'Bloque de código', atajo: 'Ctrl+Alt+C', texto: '{ }', activo: (ed) => ed.isActive('codeBlock'), accion: (ed) => ed.chain().focus().toggleCodeBlock().run() },
  { id: 'enlace', etiqueta: 'Enlace', atajo: 'Ctrl+K', texto: '🔗︎', activo: (ed) => ed.isActive('link'), accion: (ed) => pedirEnlace(ed) },
  '|',
  { id: 'deshacer', etiqueta: 'Deshacer', atajo: 'Ctrl+Z', texto: '↶', accion: (ed) => ed.chain().focus().undo().run() },
  { id: 'rehacer', etiqueta: 'Rehacer', atajo: 'Ctrl+Shift+Z', texto: '↷', accion: (ed) => ed.chain().focus().redo().run() },
];

function pedirEnlace(ed) {
  const previo = ed.getAttributes('link').href || 'https://';
  const d = document.createElement('dialog');
  d.innerHTML = `<form method="dialog" class="cuerpo-dialogo"><h2>Enlace</h2>
    <div class="tf-campo"><label for="enl-url">Dirección</label><input id="enl-url" type="url" value="${e(previo)}" inputmode="url"></div>
    <div class="acciones"><button class="tf-boton" value="quitar">Quitar enlace</button><button class="tf-boton" value="cancelar">Cancelar</button><button class="tf-boton tf-boton--principal" value="ok">Aplicar</button></div></form>`;
  document.body.appendChild(d);
  d.addEventListener('close', () => {
    const url = d.querySelector('#enl-url').value.trim();
    if (d.returnValue === 'ok' && /^(https?:|mailto:|tel:)/i.test(url)) ed.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    else if (d.returnValue === 'quitar') ed.chain().focus().extendMarkRange('link').unsetLink().run();
    else ed.commands.focus();
    d.remove();
  });
  d.showModal();
}

function colorPresencia(n) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--presencia-${(n % 5) + 1}`).trim();
  return v || '#3E4095';
}

/**
 * Crea un editor colaborativo.
 * @param {object} o  contenedor, documento {nombre, ficha, escribir}, htmlInicial, usuario {id, nombre},
 *                    etiqueta (nombre accesible), alCambiar(html), alPresencia(lista), alEstado(texto)
 */
export function crearEditor(o) {
  const { contenedor, documento, usuario } = o;
  const escribir = !!documento.escribir;
  contenedor.innerHTML = `<div class="editor-marco">
    ${escribir ? `<div class="barra-editor" role="toolbar" aria-label="Formato del texto" aria-controls="${e(documento.nombre)}">${BOTONES.map((b, i) => b === '|'
      ? '<span class="separador" aria-hidden="true"></span>'
      : `<button type="button" data-b="${b.id}" tabindex="${i === 0 ? 0 : -1}" aria-label="${e(b.etiqueta)} (${b.atajo})" title="${e(b.etiqueta)} · ${b.atajo}" ${b.activo ? 'aria-pressed="false"' : ''}>${b.texto}</button>`).join('')}</div>` : ''}
    <div class="zona-editor" id="${e(documento.nombre)}"></div></div>`;
  const zona = contenedor.querySelector('.zona-editor');
  const ydoc = new Y.Doc();
  const local = new IndexeddbPersistence('tf-' + documento.nombre, ydoc);
  const base = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/vivo/doc`;
  const proveedor = new WebsocketProvider(base, documento.nombre, ydoc, { params: { t: documento.ficha }, maxBackoffTime: 10000 });
  const color = colorPresencia(Number(usuario.id) || 0);
  let inicializado = false;

  const editor = new Editor({
    element: zona,
    editable: escribir,
    extensions: [
      StarterKit.configure({ history: false }),
      Underline,
      Link.configure({ openOnClick: !escribir, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      Placeholder.configure({ placeholder: o.marcador || 'Escribe aquí…' }),
      Collaboration.configure({ document: ydoc }),
      CollaborationCursor.configure({ provider: proveedor, user: { name: usuario.nombre, color, id: usuario.id } }),
    ],
    editorProps: { attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-label': o.etiqueta || 'Texto', ...(escribir ? {} : { 'aria-readonly': 'true' }) } },
  });

  // Si el documento colaborativo está vacío y hay una foto HTML guardada, se carga una sola vez.
  const sembrar = () => {
    if (inicializado) return;
    inicializado = true;
    if (escribir && o.htmlInicial && ydoc.getXmlFragment('default').length === 0) editor.commands.setContent(o.htmlInicial);
  };
  proveedor.on('sync', (ok) => { if (ok) { sembrar(); o.alSincronizado?.(); } });
  local.on('synced', () => { if (!navigator.onLine) sembrar(); });

  // Barra de herramientas: estado de botones y navegación con flechas (tabindex itinerante).
  const barra = contenedor.querySelector('.barra-editor');
  if (barra) {
    const botones = [...barra.querySelectorAll('button')];
    const refrescar = () => BOTONES.forEach((b) => { if (b !== '|' && b.activo) barra.querySelector(`[data-b="${b.id}"]`).setAttribute('aria-pressed', String(!!b.activo(editor))); });
    editor.on('selectionUpdate', refrescar); editor.on('transaction', refrescar);
    barra.addEventListener('click', (ev) => { const b = ev.target.closest('button'); if (!b) return; BOTONES.find((x) => x.id === b.dataset.b)?.accion(editor); });
    barra.addEventListener('keydown', (ev) => {
      const i = botones.indexOf(document.activeElement);
      let j = null;
      if (ev.key === 'ArrowRight') j = (i + 1) % botones.length; else if (ev.key === 'ArrowLeft') j = (i - 1 + botones.length) % botones.length;
      else if (ev.key === 'Home') j = 0; else if (ev.key === 'End') j = botones.length - 1;
      if (j !== null) { ev.preventDefault(); botones.forEach((b) => (b.tabIndex = -1)); botones[j].tabIndex = 0; botones[j].focus(); }
    });
    editor.view.dom.addEventListener('keydown', (ev) => {
      if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') { ev.preventDefault(); pedirEnlace(editor); }
      if (ev.altKey && ev.key === 'F10') { ev.preventDefault(); botones[0].focus(); }
    });
  }

  // Cambios: aviso al llamador (para la foto HTML) con espera de 3 s.
  let espera = null;
  ydoc.on('update', (_u, origen) => {
    if (!o.alCambiar) return;
    clearTimeout(espera);
    espera = setTimeout(() => o.alCambiar(editor.getHTML(), origen !== proveedor && origen !== local), 3000);
  });

  // Presencia: quién está en el documento.
  const presencia = () => {
    const personas = [];
    proveedor.awareness.getStates().forEach((s, id) => { if (s.user) personas.push({ ...s.user, clientId: id, yo: id === ydoc.clientID }); });
    o.alPresencia?.(personas);
  };
  proveedor.awareness.on('change', presencia);
  proveedor.on('status', ({ status }) => o.alEstado?.(status));

  return {
    editor,
    ydoc,
    html: () => editor.getHTML(),
    /** Reemplaza el contenido (restaurar versión); el cambio llega a todos los que editan. */
    reemplazar: (html) => editor.commands.setContent(html || '', true),
    /** ¿Soy quien debe crear las versiones automáticas? (el de menor clientID con permiso de escribir) */
    soyResponsable: () => {
      let menor = ydoc.clientID;
      proveedor.awareness.getStates().forEach((s, id) => { if (s.user && id < menor) menor = id; });
      return menor === ydoc.clientID;
    },
    destruir: () => { clearTimeout(espera); try { editor.destroy(); } catch { /* */ } proveedor.destroy(); local.destroy(); ydoc.destroy(); },
  };
}

export { icono };
